<template>
  <iframe name="envybox-frame" class="hidden-envy-frame"></iframe>

  <form
    ref="envyForm"
    class="callbackwidget-call-form hidden-envy-form"
    target="envybox-frame"
  >
    <input type="text" name="phone" :value="envyboxPhone" />
    <button ref="envySubmit" type="submit">Отправить</button>
  </form>
</template>

<script lang="ts">
import {API} from "@/api/generated";
import {defineComponent, nextTick} from "vue";

type EnvyboxApi = {
  settings?: {
    callingWidget?: boolean;
  };
  callToPhone?: CallableFunction;
  newCall?: CallableFunction;
};

export default defineComponent({
  name: "CallbackWidget",

  props: {
    phone: {
      type: String,
      default: ""
    },
    widgetId: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      isEnvyReady: false,
      envyWidgetId: null as string | null,
      envyInitPromise: null as Promise<boolean> | null
    };
  },

  computed: {
    envyboxPhone(): string {
      return this.normalizePhoneForEnvybox(this.phone);
    }
  },

  async mounted() {
    this.envyInitPromise = this.initEnvybox();
    await this.envyInitPromise;
  },

  watch: {
    widgetId(newWidgetId: string) {
      if (!newWidgetId || newWidgetId === this.envyWidgetId) {
        return;
      }

      this.isEnvyReady = false;
      this.envyInitPromise = this.initEnvybox();
    }
  },

  methods: {
    normalizePhoneForEnvybox(phone: string): string {
      const digits = String(phone || "").replace(/\D/g, "");

      if (digits.length < 10) {
        return "";
      }

      return digits.slice(-10);
    },

    async resolveWidgetId(): Promise<string> {
      const widgetId = this.widgetId.trim();

      if (widgetId) {
        return widgetId;
      }

      return (await API.getIntegration()).cbkiller?.[0]?.id || "";
    },

    waitForEnvyboxApi(timeout = 10000): Promise<EnvyboxApi | null> {
      return new Promise((resolve) => {
        const startedAt = Date.now();
        const tick = () => {
          const envyboxApi = (window as Window & {wsKiller?: EnvyboxApi})
            .wsKiller;

          if (
            envyboxApi &&
            (typeof envyboxApi.callToPhone === "function" ||
              typeof envyboxApi.newCall === "function")
          ) {
            resolve(envyboxApi);
            return;
          }

          if (Date.now() - startedAt >= timeout) {
            resolve(null);
            return;
          }

          setTimeout(tick, 250);
        };

        tick();
      });
    },

    async callEnvyboxDirectly(): Promise<boolean> {
      const envyboxApi = await this.waitForEnvyboxApi();

      if (!envyboxApi) {
        return false;
      }

      return await new Promise<boolean>((resolve) => {
        let resolved = false;
        const finish = (value: boolean) => {
          if (!resolved) {
            resolved = true;
            resolve(value);
          }
        };

        setTimeout(() => finish(true), 3000);

        try {
          if (envyboxApi.settings) {
            envyboxApi.settings.callingWidget = true;
          }

          if (typeof envyboxApi.callToPhone === "function") {
            envyboxApi.callToPhone(
              this.envyboxPhone,
              "",
              "",
              (response: {Success?: boolean}) =>
                finish(Boolean(response?.Success))
            );
            return;
          }

          if (typeof envyboxApi.newCall === "function") {
            envyboxApi.newCall(this.envyboxPhone);
            finish(true);
            return;
          }

          finish(false);
        } catch (error) {
          console.warn("Ошибка прямого вызова Envybox:", error);
          finish(false);
        }
      });
    },

    async initEnvybox(): Promise<boolean> {
      try {
        const idCbkiller = await this.resolveWidgetId();

        if (!idCbkiller) {
          this.isEnvyReady = false;
          return false;
        }

        this.envyWidgetId = idCbkiller;
        this.loadEnvyStyles();
        await this.loadEnvyScript(idCbkiller);
        await nextTick();

        this.isEnvyReady = true;
        return true;
      } catch (error) {
        console.error("Ошибка инициализации Envybox:", error);
        this.isEnvyReady = false;
        return false;
      }
    },

    async ensureEnvyReady(): Promise<boolean> {
      if (this.isEnvyReady) {
        return true;
      }

      if (!this.envyInitPromise) {
        this.envyInitPromise = this.initEnvybox();
      }

      const isReady = await this.envyInitPromise;
      return Boolean(isReady && this.isEnvyReady);
    },

    loadEnvyStyles(): void {
      const STYLE_ID = "envybox-cbk-style";

      if (document.getElementById(STYLE_ID)) {
        return;
      }

      const link = document.createElement("link");
      link.id = STYLE_ID;
      link.rel = "stylesheet";
      link.href = "https://cdn.envybox.io/widget/cbk.css";

      document.head.appendChild(link);
    },

    loadEnvyScript(idCbkiller: string): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        const SCRIPT_ID = "envybox-cbk-script";
        const existingScript = document.getElementById(
          SCRIPT_ID
        ) as HTMLScriptElement | null;

        if (existingScript) {
          if (existingScript.dataset.widgetId !== idCbkiller) {
            existingScript.remove();
          } else if (existingScript.dataset.loaded === "true") {
            resolve();
            return;
          } else {
            existingScript.addEventListener("load", () => resolve(), {
              once: true
            });
            existingScript.addEventListener(
              "error",
              () => reject(new Error("Ошибка загрузки скрипта Envybox")),
              {once: true}
            );
            return;
          }
        }

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.dataset.widgetId = idCbkiller;
        script.src =
          "https://cdn.envybox.io/widget/cbk.js?wcb_code=" + idCbkiller;
        script.async = true;
        script.charset = "UTF-8";

        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };

        script.onerror = () => {
          reject(new Error("Ошибка загрузки скрипта Envybox"));
        };

        document.body.appendChild(script);
      });
    },

    async submitFakeForm(): Promise<boolean> {
      if (!this.envyboxPhone) {
        return false;
      }

      const isReady = await this.ensureEnvyReady();
      if (!isReady) {
        return false;
      }

      await nextTick();

      const directCallSent = await this.callEnvyboxDirectly();
      if (directCallSent) {
        return true;
      }

      const envyForm = this.$refs.envyForm as HTMLFormElement | undefined;
      const envySubmit = this.$refs.envySubmit as HTMLButtonElement | undefined;

      if (envySubmit) {
        envySubmit.click();
      } else if (envyForm?.requestSubmit) {
        envyForm.requestSubmit();
      } else if (envyForm) {
        envyForm.submit();
      } else {
        return false;
      }

      return true;
    }
  }
});
</script>

<style>
.hidden-envy-form,
.hidden-envy-frame {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.callbackwidget-call-form input,
.callbackwidget-call-form button {
  padding: 5px;
  height: 50px;
  font-size: 20px;
  margin-right: 10px;
  color: black;
}
</style>
